// Vertical (sidebar) variant of the spotlight ad: the large card's design turned
// upright for a ~300px column — image on top, "Sponsored Building" disclosure,
// building name / street address, blurb and a "Learn More" CTA, with the whole
// card click-through. Loads through spotlightAdService (slot 3): one request per page,
// a different ad per placement (owner 2026-09-16).
//
// CITY-5292: this card is also the spec's "small" size, below the contact widget on
// condo / co-op profiles — `data-size="small"` scales it to a thumbnail there.
// Two optional attributes drive the spec rules:
//   data-reveal-at="0.3"      show only once the page is scrolled past 30% (fades in there)
//   data-page-sponsored="1"   the profile's own building is sponsoring: skip
//
// CITY-5476: this slot is shared with a Featured Listing (FL) ad, which rides along
// in the SAME /rpc/spotlight-ad response (payload `listingAd` / `listingAdWins`) —
// no second request. The sidebar takes the FL when either
//   * the building pool never reached the sidebar slot (fewer ads than placements), or
//   * the server's 50/50 coin flip (`listingAdWins`) says so for this view.
//
// CITY-5629: listing / building pages carry two copies of the small card — the desktop
// sidebar one and a `.hide-on-desktop` one above the map — and Angular runs both, hidden
// or not. They share the request and the ad object (same key, slot 3). Only a displayed
// copy counts the view and appends the advertiser's pixel (re-checked whenever its box
// changes: a window crossing the 1320px breakpoint, a cached popup attached), and the view
// is marked on the shared ad object, so the other copy showing up later does not count it again.
angular.module(NgAppName).component('crSpotlightSidebar', {
	templateUrl: 'crSpotlightSidebar.html',
	bindings: {
		section: '@',
		revealAt: '@',
		pageSponsored: '@',
		size: '@',        // 'vertical' (default) or 'small' — the compact thumbnail variant
		zoneId: '@',      // hood zone targeting — same request/cache key as crSpotlightAd on the page
		zoneIds: '@',     // CSV zone list (see crSpotlightAd)
		hoodIds: '@',     // CSV hood list (see crSpotlightAd)
		buildingId: '@',  // the page's building: advertiser-page suppression (shared request with crSpotlightAd)
		listingId: '@',   // CITY-5586: the page's listing (listing page / popup) — the Featured Listing tiers key off it
		pageId: '@',      // ADPAGE_* of the rendering page (tracking PAGE_ID)
		group: '@'        // tracking AD_GROUPING of the page ('A' main / 'B' other), same as the page's other tags
	},
	controller: ['$element', '$timeout', '$window', '$sce', 'spotlightAdService', 'spotlightAdTracking',
		function($element, $timeout, $window, $sce, spotlightAdService, spotlightAdTracking) {

		this.loaded = false;
		this.listingAd = null;
		this.revealed = true;
		// set only when the card arrives through the scroll reveal, so it fades in there
		// instead of popping into the column mid-scroll (a card shown right away is not animated)
		this.fadeIn = false;
		this.isSmall = false;
		// paid + in-term ("sponsoring"): the "Sponsored Building" disclosure is a paid-variant
		// slot, same rule as the advertiser logo on the large / medium card
		this.sponsoring = false;
		this.link = null;
		this.building = null;
		this.image = null;
		// CMS "Description / Headline" and "Text Blurb" are separate fields; either or both may be set
		this.headline = '';
		this.blurb = '';
		this.hasHeadline = false;
		this.hasBlurb = false;

		var onScroll = null;
		var viewFired = false;
		var responseData = null;

		// the card counts its view when it is actually shown (after the scroll reveal, if any).
		// CITY-5586: the Featured Listing card carries its own pixel (viewUrl, position 3 included)
		var isDisplayed = () => $element[0].getClientRects().length > 0;   // false under a display:none ancestor

		var resizeObserver = null;
		var stopObserving = () => {
			if (resizeObserver) {
				resizeObserver.disconnect();
				resizeObserver = null;
			}
			$window.removeEventListener('resize', fireViewOnce);
		};

		var fireViewOnce = () => {
			if (viewFired || !this.revealed || !isDisplayed()) {
				return;
			}
			if (this.listingAd) {
				viewFired = true;
				stopObserving();
				if (this.listingAd.viewUrl && !this.listingAd._sidebarViewFired) {
					this.listingAd._sidebarViewFired = true;   // shared with the page's other copy
					new $window.Image().src = this.listingAd.viewUrl;
				}
				return;
			}
			if (!responseData) {
				return;
			}
			viewFired = true;
			stopObserving();
			if (!responseData._sidebarViewFired) {
				responseData._sidebarViewFired = true;   // shared with the page's other copy
				spotlightAdTracking.fireView(responseData, spotlightAdTracking.positionFor(responseData, 'sidebar'));
			}
			// Advertiser's external tracking pixel: once per shared payload, from the displayed copy
			// only, after the card has rendered (the GA view event is fired by the banner component
			// only, so it is skipped here too)
			$timeout(() => {
				spotlightAdTracking.appendExternalOnce(responseData, $element[0].querySelector('.spotlight-sidebar') || $element[0]);
			});
		};

		var scrollFraction = function() {
			var doc = $window.document.documentElement;
			var max = (doc.scrollHeight || 0) - ($window.innerHeight || 0);
			if (max <= 0) return 1;
			return ($window.pageYOffset || doc.scrollTop || 0) / max;
		};

		var stopWatching = () => {
			if (onScroll) {
				$window.removeEventListener('scroll', onScroll);
				onScroll = null;
			}
		};

		var watchReveal = (fraction) => {
			this.revealed = false;
			onScroll = () => {
				if (scrollFraction() < fraction) return;
				stopWatching();
				$timeout(() => {
					this.fadeIn = true;
					this.revealed = true;
					fireViewOnce();
				});
			};
			$window.addEventListener('scroll', onScroll, { passive: true });
			onScroll();   // already past the threshold (deep link, short page)
		};

		this.$onDestroy = () => {
			stopWatching();
			stopObserving();
		};

		this.$onInit = () => {
			this.isSmall = String(this.size || '').toLowerCase() === 'small';
		};

		this.$postLink = () => {
			if (String(this.pageSponsored) === '1') {
				return;   // never advertise another building on a sponsoring building's profile
			}

			// a hidden copy that becomes the displayed one (window resized / rotated across the
			// breakpoint, a cached listing popup attached): its box goes from none to some size.
			// Stopped once the view is counted (fireViewOnce).
			if ($window.ResizeObserver) {
				resizeObserver = new $window.ResizeObserver(fireViewOnce);
				resizeObserver.observe($element[0]);
			} else {
				$window.addEventListener('resize', fireViewOnce, { passive: true });
			}

			var fraction = parseFloat(this.revealAt);
			// only a fraction strictly between 0 and 1 gates the card; 0, 1, missing or
			// invalid values mean "show as soon as the ad is loaded"
			if (!isNaN(fraction) && fraction > 0 && fraction < 1) {
				watchReveal(fraction);
			}

			// slot 3: the sidebar card shows the page's third distinct ad (empty when the pool has fewer)
			const promise = spotlightAdService.get(this.section, { zoneId: this.zoneId, zoneIds: this.zoneIds, hoodIds: this.hoodIds, buildingId: this.buildingId, listingId: this.listingId, pageId: this.pageId, group: this.group, slot: spotlightAdService.slotFor('sidebar') });
			if (!promise) return;

			promise.then((response) => {
				if (!response) return;

				// CITY-5476 / CITY-5586: the FL takes the slot when no building ad reached it, or when
				// the server's coin flip picked the listing half for this view (the server then loaded
				// one building ad fewer, so this slot has none). Its view is a real ad view (type 90).
				const building = response.data && response.data.building;
				if (response.listingAd && (!building || response.listingAdWins)) {
					this.listingAd = response.listingAd;
					this.loaded = true;
					fireViewOnce();
					return;
				}

				if (building) {
					const d = response.data;
					responseData = d;
					this.link = spotlightAdTracking.linkWithPosition(d.link, spotlightAdTracking.positionFor(d, 'sidebar'));
					fireViewOnce();
					this.building = d.building;
					this.sponsoring = !!(d.ad && d.ad.sponsoring);
					this.image = (d.gallery && d.gallery.length > 0) ? d.gallery[0] : null;

					const headline = String(d.building.headline || '').trim();
					const blurb = String(d.building.blurb || '').trim();
					this.hasHeadline = headline !== '';
					this.hasBlurb = blurb !== '';
					this.headline = $sce.trustAsHtml(headline);
					this.blurb = $sce.trustAsHtml(blurb);

					this.loaded = true;
				}
			});
		};

	}]
});
